import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ContractPricesChart = ({ contractPrices }) => {
  const [openDialog, setOpenDialog] = useState(false);

  // Transform contract prices data for the chart
  const chartData = contractPrices
    .map((price) => {
      // Parse date without timezone conversion
      const dateStr = price.date.split("T")[0];
      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day);

      return {
        date: dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        fullDate: dateObj.toLocaleDateString(),
        futuresPrice: price.futures_price
          ? parseFloat(price.futures_price)
          : null,
        flatContractPrice:
          price.futures_price && price.local_basis_price
            ? parseFloat(price.futures_price) +
              parseFloat(price.local_basis_price)
            : null,
        localBasis: price.local_basis_price
          ? parseFloat(price.local_basis_price)
          : null,
        timestamp: dateObj.getTime(), // Add timestamp for sorting
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp); // Sort chronologically (oldest to newest)

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const CustomLabel = ({ x, y, value, isMini, position = "top" }) => {
    if (value == null) return null;
    return (
      <text
        x={x}
        y={position === "bottom" ? y + 20 : y - 10}
        fill="#333"
        fontSize={isMini ? "10px" : "12px"}
        fontWeight="600"
        textAnchor="middle"
      >
        ${value.toFixed(2)}
      </text>
    );
  };

  const ChartComponent = ({ height, isMini = false }) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="date"
          stroke="#666"
          style={{ fontSize: isMini ? "12px" : "14px" }}
          angle={0}
          textAnchor="middle"
          height={60}
        />
        <YAxis
          stroke="#666"
          style={{ fontSize: isMini ? "12px" : "14px" }}
          label={{
            value: "Price ($/bu)",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: isMini ? "12px" : "14px" },
          }}
          domain={[
            (dataMin) => Math.floor(dataMin - 0.5),
            (dataMax) => Math.ceil(dataMax + 0.5),
          ]}
        />
        <Legend
          wrapperStyle={{
            paddingTop: "10px",
            fontSize: isMini ? "12px" : "14px",
          }}
        />
        <Line
          type="linear"
          dataKey="futuresPrice"
          stroke="#FB8C00"
          strokeWidth={isMini ? 2 : 3}
          name="Futures Price"
          dot={{ fill: "#FB8C00", r: isMini ? 3 : 5 }}
          activeDot={{ r: isMini ? 5 : 8 }}
          label={
            !isMini ? (
              <CustomLabel isMini={isMini} position="bottom" />
            ) : undefined
          }
        />
        <Line
          type="linear"
          dataKey="flatContractPrice"
          stroke="#26A69A"
          strokeWidth={isMini ? 2 : 3}
          name="Flat Contract Price"
          dot={{ fill: "#26A69A", r: isMini ? 3 : 5 }}
          activeDot={{ r: isMini ? 5 : 8 }}
          label={!isMini ? <CustomLabel isMini={isMini} /> : undefined}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <>
      {/* Mini Chart */}
      <Paper
        elevation={3}
        style={{
          padding: "20px",
          marginTop: "20px",
          borderRadius: "10px",
          cursor: "pointer",
          transition: "all 0.3s ease",
          position: "relative",
          border: "2px solid transparent",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.border = "2px solid #002657";
          e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.border = "2px solid transparent";
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
        }}
        onClick={handleOpenDialog}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="10px"
        >
          <Typography
            variant="h6"
            style={{ fontWeight: "bold", color: "#002657" }}
          >
            Contract Prices
          </Typography>
          <Box
            style={{
              backgroundColor: "#002657",
              borderRadius: "50%",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FullscreenIcon style={{ color: "white", fontSize: "20px" }} />
          </Box>
        </Box>
        <Typography
          variant="body2"
          style={{ color: "#666", marginBottom: "15px" }}
        >
          Click to view detailed analysis
        </Typography>
        <ChartComponent height={250} isMini={true} />
      </Paper>

      {/* Full-Screen Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            minHeight: "80vh",
            borderRadius: "15px",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#002657",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 30px",
          }}
        >
          <Box>
            <Typography variant="h5" style={{ fontWeight: "bold" }}>
              Contract Prices Analysis
            </Typography>
            <Typography
              variant="body2"
              style={{ marginTop: "5px", opacity: 0.9 }}
            >
              Historical Futures and Flat Contract Prices
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            style={{
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: "40px" }}>
          {chartData.length > 7 && (
            <Typography
              variant="body2"
              style={{
                color: "#666",
                marginBottom: "15px",
                fontStyle: "italic",
              }}
            >
              Showing latest 7 entries. Scroll left to view older data.
            </Typography>
          )}
          <Box
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              width: "100%",
              paddingBottom: "10px",
            }}
            ref={(el) => {
              if (el && chartData.length > 7) {
                el.scrollLeft = el.scrollWidth;
              }
            }}
          >
            <Box
              style={{
                minWidth:
                  chartData.length > 7 ? `${chartData.length * 120}px` : "100%",
              }}
            >
              <ChartComponent height={500} isMini={false} />
            </Box>
          </Box>

          {/* Summary Statistics */}
          <Box
            style={{
              marginTop: "30px",
              display: "flex",
              justifyContent: "space-around",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            {chartData.length > 0 && (
              <>
                <Paper
                  elevation={2}
                  style={{
                    padding: "20px",
                    flex: "1",
                    minWidth: "200px",
                    borderRadius: "10px",
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #FB8C00",
                  }}
                >
                  <Typography variant="body2" style={{ color: "#666" }}>
                    Latest Futures Price (
                    {chartData[chartData.length - 1]?.fullDate})
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: "bold",
                      color: "#FB8C00",
                      marginTop: "8px",
                    }}
                  >
                    ${chartData[chartData.length - 1]?.futuresPrice?.toFixed(2)}
                    /bu
                  </Typography>
                </Paper>
                <Paper
                  elevation={2}
                  style={{
                    padding: "20px",
                    flex: "1",
                    minWidth: "200px",
                    borderRadius: "10px",
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #26A69A",
                  }}
                >
                  <Typography variant="body2" style={{ color: "#666" }}>
                    Latest Flat Contract Price (
                    {chartData[chartData.length - 1]?.fullDate})
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: "bold",
                      color: "#26A69A",
                      marginTop: "8px",
                    }}
                  >
                    $
                    {chartData[
                      chartData.length - 1
                    ]?.flatContractPrice?.toFixed(2)}
                    /bu
                  </Typography>
                </Paper>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContractPricesChart;
